import React from 'react';
import config from '../config';

const BASE_URL = config.apiUrl;

interface Seat {
  role: string;
  model: string;
}

interface ModelsModalProps {
  open: boolean;
  onClose: () => void;
  theme: 'light' | 'dark';
  c: {
    ink: string;
    ink2: string;
    ink3: string;
    line: string;
    line2: string;
    shadow: string;
    mono: string;
  };
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType') || 'Bearer';
  return token ? { Authorization: `${tokenType} ${token}` } : {};
};

const ModelsModal: React.FC<ModelsModalProps> = ({ open, onClose, theme, c }) => {
  const [seats, setSeats] = React.useState<Seat[]>([]);
  const [economy, setEconomy] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${BASE_URL}/auth/model-lineup`, { headers: authHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (cancelled) return;
        setSeats(Array.isArray(data.seats) ? data.seats : []);
        setEconomy(data.economy === true);
      } catch {
        if (!cancelled) setError('Gagal memuat daftar model. Coba lagi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Model yang dipakai"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '520px', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          background: theme === 'dark' ? '#141619' : '#FFFFFF',
          border: `1px solid ${c.line2}`,
          borderRadius: '14px',
          boxShadow: c.shadow,
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '14px 16px 12px', borderBottom: `1px solid ${c.line}`, gap: '12px',
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink }}>Model yang dipakai</div>
            <div style={{ fontSize: '11.5px', color: c.ink3, marginTop: '2px', lineHeight: 1.4 }}>
              Dibaca langsung dari server yang sedang melayani akun ini.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Tutup"
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              color: c.ink3, fontSize: '18px', lineHeight: 1, padding: '4px 6px',
            }}
          >×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '6px 8px 10px' }}>
          {loading && (
            <div style={{ padding: '16px 8px', fontSize: '13px', color: c.ink3 }}>Memuat…</div>
          )}
          {error && (
            <div style={{ padding: '16px 8px', fontSize: '13px', color: c.ink }}>{error}</div>
          )}
          {!loading && !error && seats.map((seat) => (
            <div
              key={seat.role}
              style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: '16px', padding: '9px 8px',
                borderBottom: `1px solid ${c.line}`,
              }}
            >
              <div style={{ fontSize: '13px', color: c.ink, flexShrink: 0 }}>{seat.role}</div>
              <div style={{
                fontFamily: c.mono, fontSize: '11.5px', color: c.ink2,
                textAlign: 'right', wordBreak: 'break-all',
              }}>{seat.model}</div>
            </div>
          ))}
          {!loading && !error && (
            <div style={{ padding: '10px 8px 6px', fontSize: '11.5px', color: c.ink3 }}>
              Mode hemat: {economy ? 'nyala' : 'mati'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelsModal;
